import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router';
import styles from './Header.module.scss';
import { authStore, cartStore } from '@stores/main';
import { observer } from 'mobx-react';
import StoresAPI from '@api/stores-api';
import Product from '@models/product';
import { generateProductDetailsUrl } from '@utils/urlUtils';
import { formatDiscountPercentage } from '@utils/formatDiscountPercentage';
import { analytics } from '../../../../services/analytics';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 5; // show 5 items then "View all X products" link
const SEARCH_RESULT_IMAGE_FALLBACK = `${process.env.PUBLIC_URL ?? ''}/images/product-placeholder.svg`;

function handleSearchResultImageError(e: React.SyntheticEvent<HTMLImageElement>) {
  const target = e.currentTarget;
  target.onerror = null;
  target.src = SEARCH_RESULT_IMAGE_FALLBACK;
}

const Header = observer(() => {
  const navigate = useNavigate();
  const { cart } = cartStore;
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [profileDropdownPlacement, setProfileDropdownPlacement] = useState<{
    top: number;
    right: number;
    width: number;
  } | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const profileIconRef = useRef<HTMLButtonElement>(null);
  const searchPanelRef = useRef<HTMLDivElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const storesAPI = useMemo(() => new StoresAPI(), []);

  const goToLogin = () => {
    // Store has no own login — record the handoff to the dashboard login.
    analytics.track('login_link_clicked', { source: 'store_header' });
    if ((window as any).flutterChannel) {
      (window as any).flutterChannel.postMessage('navigateToSignIn');
    } else {
      const loginUrl = process.env.REACT_APP_LOGIN_URL;
      if (loginUrl) {
        window.location.href = loginUrl + '?redirectUrl=' + window.location.href;
      }
    }
  };

  const goToOnePlayHome = () => {
    // Cross-surface exit to the dashboard via window.location — invisible
    // to the SDK's auto link tracking, so record it (name shared with the
    // dashboard's attribute registry).
    analytics.track('back_to_home_clicked', { source: 'store_header' });
    if ((window as any).flutterChannel) {
      (window as any).flutterChannel.postMessage('navigate_back_home');
    } else {
      const oneplayHomeUrl = process.env.REACT_APP_HOME_URL;
      if (oneplayHomeUrl) {
        window.location.href = oneplayHomeUrl;
      } else {
        console.error('Oneplay Home URL is not defined');
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        profileIconRef.current &&
        !profileIconRef.current.contains(event.target as Node)
      ) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!searchExpanded || mobileSearchOpen || !searchPanelRef.current) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchPanelRef.current &&
        !searchPanelRef.current.contains(event.target as Node)
      ) {
        setSearchExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchExpanded, mobileSearchOpen]);

  const isSearchActive = searchExpanded || mobileSearchOpen;
  const wasSearchActiveRef = useRef(false);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    if (!isSearchActive) {
      wasSearchActiveRef.current = false;
      setSearchResults([]);
      setSearchTotal(0);
      return;
    }
    const runSearch = async () => {
      setSearchLoading(true);
      try {
        const { items, total } = await storesAPI.searchProducts(
          searchQuery.trim(),
          1
        );
        setSearchResults(items || []);
        setSearchTotal(total ?? 0);
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults([]);
        setSearchTotal(0);
      } finally {
        setSearchLoading(false);
      }
    };
    const justOpened = !wasSearchActiveRef.current;
    wasSearchActiveRef.current = true;
    // When user focuses/opens search (including empty bar), run immediately so results area is visible. Debounce only when query changes.
    const delay = justOpened ? 0 : SEARCH_DEBOUNCE_MS;
    searchDebounceRef.current = setTimeout(runSearch, delay);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [isSearchActive, searchQuery, storesAPI, mobileSearchOpen]);

  useLayoutEffect(() => {
    if (!mobileSearchOpen) {
      document.body.style.overflow = '';
      return;
    }
    document.body.style.overflow = 'hidden';
    setIsProfileDropdownOpen(false);
    // Defer focus until overlay is painted (iOS / WebKit reliably shows caret + keyboard).
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        mobileSearchInputRef.current?.focus({ preventScroll: true });
      });
    });
    return () => {
      window.cancelAnimationFrame(id);
      document.body.style.overflow = '';
    };
  }, [mobileSearchOpen]);

  useLayoutEffect(() => {
    if (!isProfileDropdownOpen) {
      setProfileDropdownPlacement(null);
      return;
    }
    const updatePlacement = () => {
      const trigger = profileIconRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const margin = 8;
      const minW = 220;
      const width = Math.min(Math.max(minW, rect.width), vw - margin * 2);
      const right = Math.max(margin, vw - rect.right);
      let top = rect.bottom + margin;
      const estimatedMenuHeight = 340;
      if (top + estimatedMenuHeight > vh - margin && rect.top > estimatedMenuHeight + margin) {
        top = Math.max(margin, rect.top - estimatedMenuHeight - margin);
      }
      setProfileDropdownPlacement({ top, right, width });
    };
    updatePlacement();
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => updatePlacement()) : null;
    if (ro && profileIconRef.current) {
      ro.observe(profileIconRef.current);
    }
    window.addEventListener('scroll', updatePlacement, true);
    window.addEventListener('resize', updatePlacement);
    return () => {
      ro?.disconnect();
      window.removeEventListener('scroll', updatePlacement, true);
      window.removeEventListener('resize', updatePlacement);
    };
  }, [isProfileDropdownOpen]);

  useEffect(() => {
    if (!isProfileDropdownOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsProfileDropdownOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isProfileDropdownOpen]);

  const handleSearchResultClick = useCallback(
    (product: Product) => {
      // Header inline-search suggestion click (SearchPage tracks its own).
      analytics.track('search_result_clicked', {
        product_id: product.id,
        product_title: product.title,
        query: searchQuery,
      });
      setSearchExpanded(false);
      setMobileSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      navigate(
        generateProductDetailsUrl({
          id: product.id,
          title: product.title || product.extendedTitle || 'Product',
        })
      );
    },
    [navigate, searchQuery]
  );

  const closeMobileSearch = useCallback((avoidHistoryBack = false) => {
    setMobileSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    if (!avoidHistoryBack && typeof window !== 'undefined' && window.history?.state?.searchOverlay) {
      window.history.back();
    }
  }, []);

  // Escape key closes overlay; Android back button via history
  useEffect(() => {
    if (!mobileSearchOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobileSearch();
    };
    if (!window.history.state?.searchOverlay) {
      window.history.pushState({ searchOverlay: true }, '');
    }
    window.addEventListener('keydown', handleKeyDown);
    const handlePopState = () => {
      setMobileSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [mobileSearchOpen, closeMobileSearch]);

  useEffect(() => {
    let raf = 0;
    const handleScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        setIsScrolled(window.scrollY > 50);
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const totalCount = searchTotal > 0 ? searchTotal : searchResults.length;

  const profileMenuPortal =
    isProfileDropdownOpen &&
    profileDropdownPlacement &&
    typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={dropdownRef}
            className={`${styles.profileDropdown} ${styles.profileDropdownPortal}`}
            style={{
              top: profileDropdownPlacement.top,
              right: profileDropdownPlacement.right,
              width: profileDropdownPlacement.width,
            }}
            role="menu"
            aria-label="Account menu"
          >
            {authStore.isAuthenticated ? (
              <>
                <div className={styles.dropdownUserInfo}>
                  <div
                    className={styles.userAvatar}
                    style={{
                      backgroundImage: authStore.user?.photo
                        ? `url(${authStore.user.photo})`
                        : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  >
                    {!authStore.user?.photo && <i className="fas fa-user" />}
                  </div>
                  <div className={styles.userInfoText}>
                    <div className={styles.userName}>
                      {authStore.user?.firstName} {authStore.user?.lastName}
                    </div>
                    <div className={styles.userGameName}>
                      {authStore.user?.username || 'No username set'}
                    </div>
                  </div>
                </div>
                <div className={styles.dropdownDivider} />
                <button
                  type="button"
                  className={styles.dropdownItem}
                  onClick={() => {
                    navigate('/orders');
                    setIsProfileDropdownOpen(false);
                  }}
                >
                  <i className="fas fa-shopping-bag" />
                  <span>Orders</span>
                </button>
                <button
                  type="button"
                  className={styles.dropdownItem}
                  onClick={() => {
                    navigate('/wishlist');
                    setIsProfileDropdownOpen(false);
                  }}
                >
                  <i className="fas fa-heart" />
                  <span>Wishlist</span>
                </button>
                <div className={styles.dropdownDivider} />
                <button
                  type="button"
                  className={styles.dropdownItem}
                  onClick={() => {
                    goToOnePlayHome();
                    setIsProfileDropdownOpen(false);
                  }}
                >
                  <i className="fas fa-home" />
                  <span>Back to Oneplay</span>
                </button>
              </>
            ) : (
              <>
                <div className={styles.dropdownHeader}>
                  <i className="fas fa-user-circle" />
                  <span>Guest User</span>
                </div>
                <div className={styles.dropdownDivider} />
                <button
                  type="button"
                  className={styles.dropdownItem}
                  onClick={() => {
                    goToLogin();
                    setIsProfileDropdownOpen(false);
                  }}
                >
                  <i className="fas fa-sign-in-alt" />
                  <span>Sign In/Sign Up</span>
                </button>
                <button
                  type="button"
                  className={styles.dropdownItem}
                  onClick={() => {
                    goToOnePlayHome();
                    setIsProfileDropdownOpen(false);
                  }}
                >
                  <i className="fas fa-home" />
                  <span>Back to Oneplay</span>
                </button>
              </>
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <>
    <header className={`${styles.header} ${isScrolled ? styles.scrolled : ''}`}>
      <div className={styles.headerMain}>
        <button
          type="button"
          className={styles.mobileSearchBtn}
          onClick={() => setMobileSearchOpen(true)}
          aria-label="Open search"
        >
          <i className="fas fa-search" aria-hidden />
        </button>

        <Link to="/" className={styles.logo}>
          <img src={`${process.env.PUBLIC_URL}/images/logos/oneplay-logo.svg`} alt="OnePlay Logo" />
        </Link>

        <div className={styles.headerSearchCenter}>
          <div
            ref={searchPanelRef}
            className={`${styles.searchContainer} ${styles.searchWrapper} ${searchExpanded ? styles.searchExpanded : ''}`}
          >
          <div className={styles.searchInputWrap}>
            <i className={`fas fa-search ${styles.searchIcon}`} aria-hidden />
            <input
              type="text"
              className={styles.searchBar}
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchExpanded(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchQuery.trim()) {
                  e.preventDefault();
                  navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                  setSearchExpanded(false);
                }
              }}
              aria-label="Search store"
            />
          </div>
          <div
            className={`${styles.searchSlidePanel} ${searchExpanded ? styles.searchSlidePanelOpen : ''}`}
          >
            <div className={styles.searchPanelInner}>
              {searchLoading ? (
                <div className={styles.searchPanelLoading}>Searching...</div>
              ) : searchResults.length > 0 ? (
                <>
                  <ul className={styles.searchResultsList}>
                  {searchResults.slice(0, SEARCH_LIMIT).map((product) => (
                    <li
                      key={product.id}
                      className={styles.searchResultItem}
                      onClick={() => handleSearchResultClick(product)}
                    >
                      <img
                        src={product.poster || SEARCH_RESULT_IMAGE_FALLBACK}
                        alt=""
                        className={styles.searchResultImage}
                        onError={handleSearchResultImageError}
                      />
                      <div className={styles.searchResultInfo}>
                        <span className={styles.searchResultTitle}>
                          {product.title || product.extendedTitle || 'Product'}
                        </span>
                        <div className={styles.searchResultPrices}>
                          <span className={styles.searchResultPrice}>
                            {product.buyPriceString}
                          </span>
                          {product.displayPrice > product.buyPrice && (
                            <span className={styles.searchResultPriceSlashed}>
                              {product.displayPriceString}
                            </span>
                          )}
                          {product.discountPercentage > 0 && (
                            <span className={styles.searchResultDiscount}>
                              {formatDiscountPercentage(product.discountPercentage)}% OFF
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={styles.searchViewAllLink}
                  onClick={() => {
                    setSearchExpanded(false);
                    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                  }}
                >
                  View all {totalCount} products
                </button>
                </>
              ) : searchQuery.trim() ? (
                <div className={styles.searchPanelEmpty}>No results found</div>
              ) : (
                <div className={styles.searchPanelHint}>Type to search products</div>
              )}
            </div>
          </div>
        </div>
        </div>
        
        <div className={styles.headerUtility}>
          <div className={styles.utilityLinks}></div>
          <div className={styles.headerActions}>
            {/* Show cart only when authenticated */}
            {authStore.isAuthenticated && (
              <button
                className={styles.cartIcon}
                onClick={() => navigate('/cart')}
                aria-label="Cart"
              >
                <i className="fas fa-shopping-bag"></i>
                {(cart.totalUniqueItems ?? cart.items.length) > 0 && (
                  <span className={styles.cartBadge}>{cart.totalUniqueItems ?? cart.items.length}</span>
                )}
              </button>
            )}
            
            <div className={styles.profileDropdownWrapper}>
              <button
                ref={profileIconRef}
                type="button"
                className={styles.profileIcon}
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                aria-label="Profile menu"
                aria-expanded={isProfileDropdownOpen}
                aria-haspopup="menu"
                style={{
                  backgroundImage: authStore.isAuthenticated && authStore.user?.photo 
                    ? `url(${authStore.user.photo})` 
                    : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                {(!authStore.isAuthenticated || !authStore.user?.photo) && (
                  <i className="fas fa-user"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile search full-screen overlay – rendered in portal so it's never clipped */}
      {mobileSearchOpen &&
        createPortal(
          <div
            className={styles.mobileSearchOverlay}
            role="dialog"
            aria-modal="true"
            aria-label="Search"
            tabIndex={-1}
          >
            <div className={styles.mobileSearchHeader}>
              <button
                type="button"
                className={styles.mobileSearchBack}
                onClick={() => closeMobileSearch()}
                aria-label="Close search"
              >
                <i className="fas fa-arrow-left" aria-hidden />
              </button>
              <div className={styles.mobileSearchInputWrap}>
                <i className={`fas fa-search ${styles.mobileSearchIcon}`} aria-hidden />
                <input
                  ref={mobileSearchInputRef}
                  type="search"
                  className={styles.mobileSearchInput}
                  placeholder="Search games, gift cards & more..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') closeMobileSearch();
                    if (e.key === 'Enter' && searchQuery.trim()) {
                      e.preventDefault();
                      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                      closeMobileSearch();
                    }
                  }}
                  aria-label="Search products"
                  autoComplete="off"
                  enterKeyHint="search"
                  inputMode="search"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className={styles.mobileSearchClear}
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <i className="fas fa-times-circle" aria-hidden />
                  </button>
                )}
              </div>
            </div>
            <div className={styles.mobileSearchResults}>
              {searchLoading ? (
                <div className={styles.mobileSearchLoading}>
                  <span className={styles.mobileSearchSpinner} aria-hidden />
                  <p>Searching...</p>
                </div>
              ) : searchResults.length > 0 ? (
                <>
                  <ul className={styles.mobileSearchResultsList}>
                    {searchResults.slice(0, SEARCH_LIMIT).map((product) => (
                      <li
                        key={product.id}
                        className={styles.mobileSearchResultItem}
                        onClick={() => handleSearchResultClick(product)}
                      >
                        <div className={styles.mobileSearchResultImageWrap}>
                          <img
                            src={product.poster || SEARCH_RESULT_IMAGE_FALLBACK}
                            alt=""
                            className={styles.mobileSearchResultImage}
                            onError={handleSearchResultImageError}
                          />
                        </div>
                        <div className={styles.mobileSearchResultInfo}>
                          <span className={styles.mobileSearchResultTitle}>
                            {product.title || product.extendedTitle || 'Product'}
                          </span>
                          <div className={styles.mobileSearchResultPrices}>
                            <span className={styles.mobileSearchResultPrice}>
                              {product.buyPriceString}
                            </span>
                            {product.displayPrice > product.buyPrice && (
                              <span className={styles.mobileSearchResultPriceSlashed}>
                                {product.displayPriceString}
                              </span>
                            )}
                            {product.discountPercentage > 0 && (
                              <span className={styles.mobileSearchResultDiscount}>
                                {formatDiscountPercentage(product.discountPercentage)}% OFF
                              </span>
                            )}
                          </div>
                        </div>
                        <i className={`fas fa-chevron-right ${styles.mobileSearchResultArrow}`} aria-hidden />
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className={styles.mobileSearchViewAllLink}
                    onClick={() => {
                      closeMobileSearch(true);
                      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                    }}
                  >
                    View all {totalCount} products
                  </button>
                </>
              ) : searchQuery.trim() ? (
                <div className={styles.mobileSearchEmpty}>
                  <i className="fas fa-search" aria-hidden />
                  <p>No results for &quot;{searchQuery}&quot;</p>
                  <span>Try different keywords or check spelling</span>
                </div>
              ) : (
                <div className={styles.mobileSearchHint}>
                  <i className="fas fa-search" aria-hidden />
                  <p>Search for games, gift cards & more</p>
                  <span>Start typing to see results</span>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </header>
    {profileMenuPortal}
    </>
  );
});

export default Header;

